import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { themecolors } from "../utils/themecolors";
import { useRelativeTime } from "../store";
import useWindowDimensions from "../utils/dimensions";
import TweetVideo from "./tweet_video";

// Tweet Header Components
const TweetHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 4px;
`;

const Avatar = styled.div`
  img {
    border-radius: 50%;
    width: 40px;
    height: 40px;
  }
`;

const UserInfo = styled.div`
  margin-left: 10px;
  line-height: 1.3;
`;

const DisplayName = styled.div`
  font-weight: 600;
  font-size: 15px;
`;

const UserMeta = styled.div`
  font-size: 14px;
  color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_medium_impact_text};
  display: flex;
  align-items: center;
`;

const Dot = styled.span`
  font-size: 8px;
  padding: 0 4px;
`;

// Tweet Content Components
const TweetContent = styled.div`
  font-size: 15px;
  padding-top: 8px;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

// Media Components
const MediaContainer = styled.div`
  margin-top: 12px;
  margin-left: -16px;
  margin-right: -16px;
  margin-bottom: ${props => props.isLastElement ? '-16px' : '12px'};
  width: calc(100% + 32px);
  overflow: hidden;
`;

// Single Image
const SingleImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
`;

// Image Gallery
const Gallery = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2px;
  overflow: hidden;
  aspect-ratio: ${props => props.count === 3 ? '7/8' : '16/9'};
  
  &[data-count="2"] {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: 1fr;
  }

  &[data-count="3"] {
    grid-template-areas:
      "img1 img2"
      "img1 img3";
  }

  &[data-count="4"] {
    grid-template-areas:
      "img1 img2"
      "img3 img4";
  }
`;

const GalleryImage = styled.div`
  height: 100%;

  &:nth-child(1) {
    grid-area: ${({count}) => count === 3 ? 'img1' : 'auto'};
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

// Video Components
const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  background-color: #000;

  video {
    width: 100%;
    height: 100%;
    display: block;
  }
`;

const VideoErrorMessage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.7);
  color: ${themecolors.dark_high_impact_text};

  button {
    margin-top: 8px;
    padding: 6px 12px;
    background-color: ${themecolors.blue};
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
  }
`;

// Poll Components
const PollContainer = styled.div`
  margin-top: 12px;
`;

const PollOption = styled.div`
  position: relative;
  height: 36px;
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PollBar = styled.div`
  position: absolute;
  height: 100%;
  border-radius: 4px;
  background-color: ${({isWinner, theme}) =>
          isWinner
                  ? 'rgba(29, 155, 240, 0.58)'
                  : theme.color.name === 'dark'
                  ? themecolors.dark_background_7
                  : themecolors.lighter_border};
`;

const PollText = styled.div`
  z-index: 1;
  padding: 0 12px;
  font-weight: 500;
`;

const PollPercentage = styled.div`
  z-index: 1;
  padding: 0 12px;
  font-weight: 500;
`;

// Link Embed Components
const LinkEmbedContainer = styled.div`
  margin-top: 12px;
  border: 1px solid ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_7 : themecolors.lighter_border};
  border-radius: 12px;
  overflow: hidden;
`;

const LinkEmbedImage = styled.div`
  height: 160px;
  background-size: cover;
  background-position: center;
  border-bottom: 1px solid ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_7 : themecolors.lighter_border};
`;

const LinkEmbedContent = styled.div`
  padding: 12px;
`;

const LinkEmbedDomain = styled.div`
  font-size: 14px;
  color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_medium_impact_text};
  margin-bottom: 4px;
`;

const LinkEmbedTitle = styled.div`
  font-size: 15px;
  font-weight: 500;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

// Main Tweet Container
const TweetContainer = styled.div`
  border: 1px solid ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_7 : themecolors.lighter_border};
  border-radius: 12px;
  max-width: 540px;
  background-color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_1 : '#fff'};
  padding: 16px;
  margin: ${({isQuoted}) => isQuoted ? '12px 0' : '0'};
  color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_high_impact_text : themecolors.light_high_impact_text};
  overflow: hidden;
  margin-top: 12px;

  a {
    text-decoration: none;
    color: inherit;
  }
`;

const TweetEmbed = ({ data, isQuoted = false }) => {
    const { width } = useWindowDimensions();
    const [videoError, setVideoError] = useState(false);
    const mediaRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const relativeTime = useRelativeTime(data?.created, false);

    useEffect(() => {
        if (mediaRef.current) {
            setContainerWidth(mediaRef.current.offsetWidth);
        }
    }, [width]);

    if (!data) return null;

    const handleVideoError = () => setVideoError(true);
    const resetVideoError = () => setVideoError(false);

    // Determine if media is the last element in the tweet
    const hasContent = !!data.content;
    const hasImages = !!data.images?.length;
    const hasVideo = !!data.video;
    const hasPoll = !!data.poll;
    const hasLinkEmbed = !!data.linkEmbed;
    const hasQuotedTweet = !isQuoted && !!data.quotedTweet;

    const isImagesLastElement = hasImages && !hasVideo && !hasPoll && !hasLinkEmbed && !hasQuotedTweet;
    const isVideoLastElement = hasVideo && !hasPoll && !hasLinkEmbed && !hasQuotedTweet;

    // Calculate image dimensions
    const calculateImageDimensions = (image) => {
        if (!containerWidth) return null;
        
        const maxHeight = 480;
        const { width, height } = image;

        // First scale based on max height if needed
        const scaleFactor = height > maxHeight ? maxHeight / height : 1;
        let scaledWidth = width * scaleFactor;
        let scaledHeight = height * scaleFactor;

        // Then scale based on container width if needed
        if (scaledWidth > containerWidth) {
            const widthScaleFactor = containerWidth / scaledWidth;
            scaledWidth = containerWidth;
            scaledHeight *= widthScaleFactor;
        }

        return {
            width: Math.round(scaledWidth),
            height: Math.round(scaledHeight)
        };
    };

    return (
        <TweetContainer isQuoted={isQuoted} onClick={(e) => e.stopPropagation()}>
            <a href={`https://x.com/${data.user.username}/status/${data.id}`} target="_blank" rel="noopener noreferrer">
                <TweetHeader>
                    <Avatar>
                        <img src={data.user.profilePicture} alt={data.user.displayName} />
                    </Avatar>
                    <UserInfo>
                        <DisplayName>{data.user.displayName}</DisplayName>
                        <UserMeta>
                            @{data.user.username} <Dot>•</Dot> {relativeTime}
                        </UserMeta>
                    </UserInfo>
                </TweetHeader>
            </a>

            {data.content && (
                <TweetContent>
                    {data.content}
                </TweetContent>
            )}

            {/* Media: Single Image */}
            {data.images?.length === 1 && (
                <MediaContainer ref={mediaRef} isLastElement={isImagesLastElement}>
                    <a href={`https://x.com/${data.user.username}/status/${data.id}`} target="_blank" rel="noopener noreferrer">
                        <SingleImageContainer>
                            {(() => {
                                const dimensions = calculateImageDimensions(data.images[0]);
                                if (!dimensions) return null;

                                return (
                                    <div style={{
                                        width: `${dimensions.width}px`,
                                        height: `${dimensions.height}px`,
                                        maxWidth: '100%'
                                    }}>
                                        <img
                                            src={data.images[0].url}
                                            alt=""
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    </div>
                                );
                            })()}
                        </SingleImageContainer>
                    </a>
                </MediaContainer>
            )}

            {/* Media: Multiple Images */}
            {data.images?.length > 1 && (
                <MediaContainer isLastElement={isImagesLastElement}>
                    <a href={`https://x.com/${data.user.username}/status/${data.id}`} target="_blank" rel="noopener noreferrer">
                        <Gallery data-count={data.images.length}>
                            {data.images.slice(0, 4).map((image, index) => (
                                <GalleryImage key={index} count={data.images.length}>
                                    <img src={image.url} alt="" />
                                </GalleryImage>
                            ))}
                        </Gallery>
                    </a>
                </MediaContainer>
            )}

            {/* Media: Video */}
            {data.video && (
                <MediaContainer isLastElement={isVideoLastElement}>
                    <VideoWrapper style={{
                        paddingTop: `${data.video.heightRatio > 120 ? 100 : data.video.heightRatio}%`
                    }}>
                        {videoError ? (
                            <VideoErrorMessage>
                                <div>Video failed to load.</div>
                                <button type="button" onClick={resetVideoError}>Retry</button>
                            </VideoErrorMessage>
                        ) : (
                            data.video.mpeg ? (
                                <TweetVideo data={data} />
                            ) : (
                                <video controls playsInline onError={handleVideoError}>
                                    <source
                                        src={`${data.video.url}#t=0.001`}
                                        type="video/mp4"
                                        referrerPolicy="no-referrer"
                                    />
                                    Your browser does not support the video tag.
                                </video>
                            )
                        )}
                    </VideoWrapper>
                </MediaContainer>
            )}

            {/* Poll */}
            {data.poll && (
                <PollContainer>
                    {data.poll.options.map((option, index) => {
                        const isWinner = option.percentage === data.poll.winningPercentage;
                        return (
                            <PollOption key={index}>
                                <PollBar
                                    style={{ width: `${option.percentage}%` }}
                                    isWinner={isWinner}
                                />
                                <PollText>{option.label}</PollText>
                                <PollPercentage>{option.percentage}%</PollPercentage>
                            </PollOption>
                        );
                    })}
                </PollContainer>
            )}

            {/* Link Embed */}
            {data.linkEmbed && (
                <LinkEmbedContainer>
                    <a href={data.linkEmbed.url} target="_blank" rel="noopener noreferrer">
                        {data.linkEmbed.image && (
                            <LinkEmbedImage style={{ backgroundImage: `url(${data.linkEmbed.image})` }} />
                        )}
                        <LinkEmbedContent>
                            <LinkEmbedDomain>{data.linkEmbed.domain}</LinkEmbedDomain>
                            <LinkEmbedTitle>{data.linkEmbed.title}</LinkEmbedTitle>
                        </LinkEmbedContent>
                    </a>
                </LinkEmbedContainer>
            )}

            {/* Quoted Tweet */}
            {!isQuoted && data.quotedTweet && (
                <TweetEmbed data={data.quotedTweet} isQuoted />
            )}
        </TweetContainer>
    );
};

export default TweetEmbed;
